import { type FormEvent, type ReactElement, useCallback, useEffect, useState } from 'react';
import { type ApiError, type WidgetEmbedView, type WidgetAudience, type WidgetPosition } from '@vello/api-client';
import {
  WIDGET_FORM_MAX_ORIGINS,
  WIDGET_FORM_NAME_MAX,
  WIDGET_FORM_CONSENT_MAX,
  WIDGET_FORM_AUDIENCES,
  widgetAudienceTopics,
  createInputOf,
  emptyWidgetForm,
  formatMarketInstant,
  originsSummary,
  scopeTimeZone,
  validateWidgetForm,
  widgetAuthority,
  widgetNeedsMarket,
  widgetFormFromView,
  widgetIssueOfReason,
  widgetPatchOf,
  widgetSnippetState,
  widgetStatusTone,
  type WidgetForm,
  type WidgetScope,
  type WidgetFormField,
  type WidgetFormIssue,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, CheckRow, Select, Drawer, EmptyResult, Field, IconButton, ListTable, Notice, PageHeader, SegmentedControl, TextArea, TextInput, DrawerHeader, DrawerBody, DrawerFooter, Cluster, Stack, Text, Divider, DrawerForm, Spacer } from '@vello/web-theme';
import { LoadingBlock } from '../components/loading-block';

/**
 * Configurações › Widgets (D429 W-EMBED-ADMIN, program D425, ADR-0067 + Amendment D429) — the Central widget's embeds.
 *
 * - The list names each embed, its key's display prefix, its allowed sites, its status and when it last changed. A row
 *   opens the record drawer.
 * - "Novo widget" (the screen's ONE primary action, only with `widget:manage`) opens a form validated with the kernel's
 *   own rules (exact `https://host[:port]` origins, no wildcard, 1..20; a one-line name; a non-empty subject set; an
 *   optional language tag and consent text). A server `WIDGET_*` refusal lands on its field.
 * - The record shows the ready `<script>` snippet with a Copy button at ANY time (the server keeps the public key
 *   retrievable — Amendment D429) and how to install it; a pre-m0134 embed says "change the key to show the code"; a
 *   revoked one is read-only and has no code. The settings save with the view's revision (a 409 reloads the current
 *   version and says so). "Trocar chave" and "Revogar" sit behind the in-app confirmation (never a native dialog), each
 *   saying what stops working and for whom.
 * - Without `widget:manage` the screen is read-only (the code can still be copied); the server authorizes every call.
 */

function useWhen(): (iso: string | undefined) => string {
  const { scope, markets } = useAdmin();
  const { lang } = useT();
  return (iso) => formatMarketInstant(iso, scopeTimeZone(scope.current, markets), lang);
}

/** The words of a failed call: a field issue when the server named one, else a sentence (never a bare code). */
function useRefusal(): (e: ApiError) => { issue?: WidgetFormIssue; text: string } {
  const { t } = useT();
  const { fail } = useVocab();
  return (e) => {
    const issue = widgetIssueOfReason(e.reasonCode);
    if (issue !== undefined) return { issue, text: t(`widgets.issue.${issue.code}`) };
    if (e.code === 'NOT_FOUND') return { text: t('widgets.error.notFound') };
    if (e.code === 'FORBIDDEN') return { text: t('widgets.error.forbidden') };
    return { text: t('widgets.error.action', { code: fail(e) }) };
  };
}

// ---- the form fields (create + edit) ------------------------------------------------------------------------------

function WidgetFormFields({ idPrefix, form, onChange, issues, disabled, readOnly = false }: { idPrefix: string; form: WidgetForm; onChange: (f: WidgetForm) => void; issues: readonly WidgetFormIssue[]; disabled: boolean; readOnly?: boolean }): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const { markets } = useAdmin();
  const issueFor = (field: WidgetFormField, index?: number): WidgetFormIssue | undefined => issues.find((i) => i.field === field && i.index === index);
  const say = (i: WidgetFormIssue | undefined, id: string): ReactElement | null => (i === undefined ? null : <span id={id} role="alert" className="vello-field__error">{t(`widgets.issue.${i.code}`)}</span>);
  const set = <K extends keyof WidgetForm>(k: K, v: WidgetForm[K]): void => onChange({ ...form, [k]: v });
  const nameIssue = issueFor('name');
  const originsIssue = issueFor('origins');
  const marketIssue = issueFor('marketId');
  const countryIssue = issueFor('countryCode');
  // D853: the countries the deployment operates in, from its markets (a country with no market yet cannot be chosen here).
  const countries = [...new Set(markets.map((m) => m.countryCode))].sort();
  // an existing embed keeps its scope: a city embed cannot lose its market (the server refuses it)
  const locked = readOnly || idPrefix === 'widget-edit';
  const audiencesIssue = issueFor('audiences');
  const localeIssue = issueFor('locale');
  const consentIssue = issueFor('consentText');
  const settingsIssue = issueFor('settings');
  // D625: the audiences keep the catalog's order (the widget lists them in it).
  const toggleAudience = (a: WidgetAudience, on: boolean): void =>
    set('audiences', WIDGET_FORM_AUDIENCES.filter((x) => (x === a ? on : form.audiences.includes(x))));
  const topicsOf = (a: WidgetAudience): string => widgetAudienceTopics(a).map((topic) => v('widget.topic', topic)).join(', ');

  return (
    <>
      <Field label={t('widgets.field.name')} htmlFor={`${idPrefix}-name`} hint={t('widgets.field.name.hint')}>
        <TextInput
          id={`${idPrefix}-name`}
          value={form.name}
          maxLength={WIDGET_FORM_NAME_MAX}
          disabled={disabled}
          autoComplete="off"
          aria-invalid={nameIssue !== undefined}
          aria-describedby={nameIssue !== undefined ? `${idPrefix}-name-error` : undefined}
          onChange={(e) => set('name', e.target.value)}
        />
        {say(nameIssue, `${idPrefix}-name-error`)}
      </Field>

      <Stack as="fieldset" gap={2} aria-describedby={`${idPrefix}-origins-hint`}>
        <Text as="legend" role="label" tone="secondary">{t('widgets.field.origins')}</Text>
        {form.origins.map((origin, index) => {
          const rowIssue = issueFor('origins', index);
          const errId = `${idPrefix}-origin-${index}-error`;
          return (
            <div key={index}>
              <Cluster gap={2}>
                <TextInput
                  id={`${idPrefix}-origin-${index}`}
                  aria-label={t('widgets.origin.label', { n: index + 1 })}
                  className="tnum"
                  value={origin}
                  placeholder="https://"
                  inputMode="url"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={disabled}
                  aria-invalid={rowIssue !== undefined}
                  aria-describedby={rowIssue !== undefined ? errId : undefined}
                  onChange={(e) => set('origins', form.origins.map((o, i) => (i === index ? e.target.value : o)))}
                />
                {!readOnly && form.origins.length > 1 && (
                  <IconButton icon="x" label={t('widgets.origin.remove', { n: index + 1 })} onClick={() => set('origins', form.origins.filter((_, i) => i !== index))} />
                )}
              </Cluster>
              {say(rowIssue, errId)}
            </div>
          );
        })}
        {!readOnly && form.origins.length < WIDGET_FORM_MAX_ORIGINS && (
          <Button variant="ghost" size="sm" disabled={disabled} onClick={() => set('origins', [...form.origins, ''])}>{t('widgets.origin.add')}</Button>
        )}
        <Text role="caption" tone="tertiary" block id={`${idPrefix}-origins-hint`}>{t('widgets.field.origins.hint')}</Text>
        {say(originsIssue, `${idPrefix}-origins-error`)}
      </Stack>

      <div>
        <Text role="label" tone="secondary" block id={`${idPrefix}-scope-label`}>{t('widgets.field.scope')}</Text>
        {locked ? (
          <span>{t(`widgets.scope.${form.scope}`)}</span>
        ) : (
          <SegmentedControl<WidgetScope>
            ariaLabel={t('widgets.field.scope')}
            value={form.scope}
            onChange={(sc) => set('scope', sc)}
            options={[{ id: 'country', label: t('widgets.scope.country') }, { id: 'city', label: t('widgets.scope.city') }]}
          />
        )}
        <Text role="caption" tone="tertiary" block>{t('widgets.field.scope.hint')}</Text>
      </div>

      {form.scope === 'country' && (
        <Field label={t('widgets.field.country')} htmlFor={`${idPrefix}-country`}>
          <Select
            id={`${idPrefix}-country`}
            value={form.countryCode}
            disabled={disabled}
            aria-invalid={countryIssue !== undefined}
            aria-describedby={countryIssue !== undefined ? `${idPrefix}-country-error` : undefined}
            onChange={(e) => set('countryCode', e.target.value)}
          >
            <option value="">{t('widgets.field.country.choose')}</option>
            {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            {form.countryCode !== '' && !countries.includes(form.countryCode) && <option value={form.countryCode}>{form.countryCode}</option>}
          </Select>
          {say(countryIssue, `${idPrefix}-country-error`)}
        </Field>
      )}

      {form.scope === 'city' && (
        <Field label={t('widgets.field.market')} htmlFor={`${idPrefix}-market`} hint={t('widgets.field.market.hint')}>
          <Select
            id={`${idPrefix}-market`}
            value={form.marketId}
            disabled={disabled}
            aria-invalid={marketIssue !== undefined}
            aria-describedby={marketIssue !== undefined ? `${idPrefix}-market-error` : undefined}
            onChange={(e) => set('marketId', e.target.value)}
          >
            <option value="">{t('widgets.field.market.choose')}</option>
            {markets.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            {form.marketId !== '' && !markets.some((m) => m.id === form.marketId) && <option value={form.marketId}>{form.marketId}</option>}
          </Select>
          {say(marketIssue, `${idPrefix}-market-error`)}
        </Field>
      )}

      <div>
        <Text role="label" tone="secondary" block id={`${idPrefix}-position-label`}>{t('widgets.field.position')}</Text>
        {readOnly ? (
          <span>{v('widget.position', form.position)}</span>
        ) : (
          <SegmentedControl<WidgetPosition>
            ariaLabel={t('widgets.field.position')}
            value={form.position}
            onChange={(p) => set('position', p)}
            options={[{ id: 'right', label: v('widget.position', 'right') }, { id: 'left', label: v('widget.position', 'left') }]}
          />
        )}
      </div>

      <Stack as="fieldset" gap={2}>
        <Text as="legend" role="label" tone="secondary">{t('widgets.field.audiences')}</Text>
        {readOnly ? (
          <span>{form.audiences.map((a) => v('widget.audience', a)).join(', ')}</span>
        ) : (
          <Stack gap={1}>
            {WIDGET_FORM_AUDIENCES.map((a) => (
              <Stack key={a}>
                <CheckRow id={`${idPrefix}-audience-${a}`} checked={form.audiences.includes(a)} onChange={(on) => !disabled && toggleAudience(a, on)} describedBy={`${idPrefix}-audience-${a}-topics`}>
                  {v('widget.audience', a)}
                </CheckRow>
                <Text id={`${idPrefix}-audience-${a}-topics`} role="caption" tone="secondary">{topicsOf(a)}</Text>
              </Stack>
            ))}
          </Stack>
        )}
        <Text role="caption" tone="secondary">{t('widgets.field.audiences.hint')}</Text>
        {say(audiencesIssue, `${idPrefix}-audiences-error`)}
      </Stack>

      <Field label={t('widgets.field.locale')} htmlFor={`${idPrefix}-locale`} hint={t('widgets.field.locale.hint')}>
        <TextInput
          id={`${idPrefix}-locale`}
          value={form.locale}
          maxLength={35}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          aria-invalid={localeIssue !== undefined}
          aria-describedby={localeIssue !== undefined ? `${idPrefix}-locale-error` : undefined}
          onChange={(e) => set('locale', e.target.value)}
        />
        {say(localeIssue, `${idPrefix}-locale-error`)}
      </Field>

      <Field label={t('widgets.field.consent')} htmlFor={`${idPrefix}-consent`} hint={t('widgets.field.consent.hint')}>
        <TextArea
          id={`${idPrefix}-consent`}
          value={form.consentText}
          maxLength={WIDGET_FORM_CONSENT_MAX}
          disabled={disabled}
          aria-invalid={consentIssue !== undefined}
          aria-describedby={consentIssue !== undefined ? `${idPrefix}-consent-error` : undefined}
          onChange={(e) => set('consentText', e.target.value)}
        />
        {say(consentIssue, `${idPrefix}-consent-error`)}
      </Field>
      {settingsIssue !== undefined && <Notice tone="danger">{t(`widgets.issue.${settingsIssue.code}`)}</Notice>}
    </>
  );
}

// ---- the code block ------------------------------------------------------------------------------------------------

function SnippetBlock({ view, notice }: { view: WidgetEmbedView; notice?: string }): ReactElement {
  const { t } = useT();
  const { toast } = useConsole();
  const [fallback, setFallback] = useState(false);
  const state = widgetSnippetState(view);
  useEffect(() => setFallback(false), [view.snippet]);

  if (state.kind === 'revoked') return <Notice tone="neutral">{t('widgets.code.revoked')}</Notice>;
  if (state.kind === 'rotate_to_show') return <Notice tone="warning">{t('widgets.code.rotateToShow')}</Notice>;

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(state.snippet);
      setFallback(false);
      toast({ message: t('widgets.code.copied'), tone: 'success' });
    } catch {
      // Clipboard blocked (permissions / insecure context): select the code so the operator copies it by hand.
      const area = document.getElementById(`widget-snippet-${view.embedId}`);
      if (area instanceof HTMLTextAreaElement) {
        area.focus();
        area.select();
      }
      setFallback(true);
    }
  };

  return (
    <Stack gap={3}>
      {notice !== undefined && <Notice tone="warning">{notice}</Notice>}
      <Text as="label" role="label" tone="secondary" htmlFor={`widget-snippet-${view.embedId}`}>{t('widgets.code.label')}</Text>
      <TextArea
        id={`widget-snippet-${view.embedId}`}
        readOnly
        value={state.snippet}
        rows={4}
        spellCheck={false}
        onFocus={(e) => e.currentTarget.select()}
        className="vello-ab-code"
      />
      <Button variant="secondary" onClick={() => void copy()}>{t('widgets.code.copy')}</Button>
      {fallback && <Notice tone="warning">{t('widgets.code.copyFailed')}</Notice>}
      <Text as="p" role="caption" tone="tertiary">{t('widgets.code.install')}</Text>
    </Stack>
  );
}

// ---- create --------------------------------------------------------------------------------------------------------

function CreateWidgetDrawer({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (view: WidgetEmbedView) => void }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const refusal = useRefusal();
  const [form, setForm] = useState<WidgetForm>(() => emptyWidgetForm());
  const [issues, setIssues] = useState<readonly WidgetFormIssue[]>([]);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(emptyWidgetForm());
    setIssues([]);
    setMessage(undefined);
  }, [open]);

  const submit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMessage(undefined);
    const valid = validateWidgetForm(form);
    if (!valid.ok) {
      setIssues(valid.error);
      return;
    }
    setIssues([]);
    setBusy(true);
    const res = await runtime.widgets.create(createInputOf(valid.value));
    setBusy(false);
    if (res.ok) {
      onCreated(res.value.embed);
      return;
    }
    const r = refusal(res.error);
    if (r.issue !== undefined) setIssues([r.issue]);
    else setMessage(r.text);
  };

  return (
    <Drawer open={open} onClose={onClose} labelledBy="widget-new-title">
      <DrawerForm onSubmit={(e) => void submit(e)} noValidate>
          <DrawerHeader titleId="widget-new-title" title={t('widgets.create.title')} subtitle={t('widgets.create.subtitle')} />
          <DrawerBody>
            {message !== undefined && <Notice tone="danger">{message}</Notice>}
            <WidgetFormFields idPrefix="widget-new" form={form} onChange={setForm} issues={issues} disabled={busy} />
          </DrawerBody>
          <DrawerFooter>
            <Button variant="ghost" onClick={onClose}>{t('ui.cancel')}</Button>
            <Spacer />
            <Button type="submit" variant="primary" disabled={busy}>{t('widgets.create.submit')}</Button>
          </DrawerFooter>
      </DrawerForm>
    </Drawer>
  );
}

// ---- detail / edit -------------------------------------------------------------------------------------------------

function WidgetDetailDrawer({ embed, mayManage, onClose, onChanged }: { embed: WidgetEmbedView | undefined; mayManage: boolean; onClose: () => void; onChanged: (view: WidgetEmbedView) => void }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v } = useVocab();
  const { toast, confirm } = useConsole();
  const refusal = useRefusal();
  const when = useWhen();
  const [view, setView] = useState<WidgetEmbedView | undefined>(embed);
  const [form, setForm] = useState<WidgetForm>(() => (embed !== undefined ? widgetFormFromView(embed) : emptyWidgetForm()));
  const [issues, setIssues] = useState<readonly WidgetFormIssue[]>([]);
  const [message, setMessage] = useState<{ tone: 'danger' | 'warning'; text: string } | undefined>(undefined);
  const [codeNotice, setCodeNotice] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const embedId = embed?.embedId;

  const adopt = useCallback(
    (next: WidgetEmbedView): void => {
      setView(next);
      setForm(widgetFormFromView(next));
      onChanged(next);
    },
    [onChanged],
  );

  // Open: show what the list had at once, then read the current version (revision + code) from the server.
  useEffect(() => {
    if (embed === undefined || embedId === undefined) return;
    setView(embed);
    setForm(widgetFormFromView(embed));
    setIssues([]);
    setMessage(undefined);
    setCodeNotice(undefined);
    let live = true;
    void runtime.widgets.get(embedId).then((res) => {
      if (!live) return;
      if (res.ok) {
        setView(res.value.embed);
        setForm(widgetFormFromView(res.value.embed));
      } else setMessage({ tone: 'danger', text: refusal(res.error).text });
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedId]);

  const reload = async (): Promise<void> => {
    if (embedId === undefined) return;
    const res = await runtime.widgets.get(embedId);
    if (res.ok) adopt(res.value.embed);
  };

  const active = view?.status === 'ACTIVE';
  const editable = mayManage && active;
  const dirty = view !== undefined && JSON.stringify(form) !== JSON.stringify(widgetFormFromView(view));

  const save = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (view === undefined || !editable) return;
    setMessage(undefined);
    const valid = validateWidgetForm(form);
    if (!valid.ok) {
      setIssues(valid.error);
      return;
    }
    setIssues([]);
    const patch = widgetPatchOf(view, valid.value);
    if (patch === undefined) return;
    setBusy(true);
    const res = await runtime.widgets.update(view.embedId, patch);
    setBusy(false);
    if (res.ok) {
      const before = view.snippet;
      adopt(res.value.embed);
      setCodeNotice(before !== undefined && res.value.embed.snippet !== undefined && before !== res.value.embed.snippet ? t('widgets.code.changed') : undefined);
      toast({ message: t('widgets.saved'), tone: 'success' });
      return;
    }
    if (res.error.code === 'CONFLICT') {
      await reload();
      setMessage({ tone: 'warning', text: res.error.reasonCode === 'WIDGET_EMBED_REVOKED' ? t('widgets.code.revoked') : t('widgets.conflict') });
      return;
    }
    const r = refusal(res.error);
    if (r.issue !== undefined) setIssues([r.issue]);
    else setMessage({ tone: 'danger', text: r.text });
  };

  const rotate = async (): Promise<void> => {
    if (view === undefined) return;
    const answer = await confirm({
      title: t('widgets.rotate.confirm.title', { name: view.name }),
      body: t('widgets.rotate.confirm.body'),
      confirmLabel: t('widgets.key.rotate'),
      cancelLabel: t('ui.cancel'),
      icon: 'rotateCcw',
    });
    if (!answer.confirmed) return;
    setBusy(true);
    setMessage(undefined);
    const res = await runtime.widgets.rotate(view.embedId);
    setBusy(false);
    if (res.ok) {
      adopt(res.value.embed);
      setCodeNotice(t('widgets.rotate.done'));
      toast({ message: t('widgets.rotate.done'), tone: 'success' });
    } else {
      if (res.error.code === 'CONFLICT') await reload();
      setMessage({ tone: 'danger', text: refusal(res.error).text });
    }
  };

  const revoke = async (): Promise<void> => {
    if (view === undefined) return;
    const answer = await confirm({
      title: t('widgets.revoke.confirm.title', { name: view.name }),
      body: t('widgets.revoke.confirm.body', { sites: view.allowedOrigins.join(', ') }),
      confirmLabel: t('widgets.revoke.confirm.ok'),
      cancelLabel: t('ui.cancel'),
      tone: 'danger',
      icon: 'ban',
    });
    if (!answer.confirmed) return;
    setBusy(true);
    setMessage(undefined);
    const res = await runtime.widgets.revoke(view.embedId);
    setBusy(false);
    if (res.ok) {
      adopt(res.value.embed);
      setCodeNotice(undefined);
      toast({ message: t('widgets.revoke.done'), tone: 'success' });
    } else setMessage({ tone: 'danger', text: refusal(res.error).text });
  };

  return (
    <Drawer open={embed !== undefined} onClose={onClose} labelledBy="widget-detail-title">
      {view !== undefined && (
        <DrawerForm onSubmit={(e) => void save(e)} noValidate>
            <DrawerHeader
              titleId="widget-detail-title"
              title={view.name}
              subtitle={
                <>
                  <span>{t('widgets.detail.subtitle', { when: when(view.createdAt) })}</span>
                  {view.revokedAt !== undefined && <Text block>{t('widgets.revokedAt', { when: when(view.revokedAt) })}</Text>}
                </>
              }
              actions={<Badge tone={widgetStatusTone(view.status)}>{v('widget.embed_status', view.status)}</Badge>}
              onClose={onClose}
              closeLabel={t('ui.close')}
            />

            <DrawerBody>
              {message !== undefined && <Notice tone={message.tone}>{message.text}</Notice>}
              {widgetNeedsMarket(view) && <Notice tone="danger">{t('widgets.market.missing')}</Notice>}

              <Stack as="section" gap={3} aria-labelledby="widget-code-title">
                <Text as="h3" role="label" id="widget-code-title">{t('widgets.code.title')}</Text>
                <SnippetBlock view={view} {...(codeNotice !== undefined ? { notice: codeNotice } : {})} />
              </Stack>
              <Divider />

              <Stack as="section" gap={3} aria-labelledby="widget-settings-title">
                <Text as="h3" role="label" id="widget-settings-title">{t('widgets.settings.title')}</Text>
                <WidgetFormFields idPrefix="widget-edit" form={form} onChange={setForm} issues={issues} disabled={!editable || busy} readOnly={!editable} />
              </Stack>
              <Divider />

              <Stack as="section" gap={3} aria-labelledby="widget-key-title">
                <Text as="h3" role="label" id="widget-key-title">{t('widgets.key.title')}</Text>
                <Text as="p" mono>{view.keyDisplay}</Text>
                {view.rotatedAt !== undefined && <Text role="caption" tone="tertiary" block>{t('widgets.key.rotatedAt', { when: when(view.rotatedAt) })}</Text>}
                <Text as="p" role="caption" tone="tertiary">{t('widgets.key.explain')}</Text>
                {editable && <Button variant="secondary" icon="rotateCcw" disabled={busy} onClick={() => void rotate()}>{t('widgets.key.rotate')}</Button>}
              </Stack>

              {editable && (
                <section aria-labelledby="widget-revoke-title">
                  <Text as="h3" role="label" id="widget-revoke-title">{t('widgets.revoke.title')}</Text>
                  <Text as="p" role="caption" tone="tertiary">{t('widgets.revoke.explain')}</Text>
                  <Button variant="danger" icon="ban" disabled={busy} onClick={() => void revoke()}>{t('widgets.revoke.button')}</Button>
                </section>
              )}
            </DrawerBody>

            <DrawerFooter>
              <Button variant="ghost" onClick={onClose}>{t('ui.close')}</Button>
              <Spacer />
              {editable && <Button type="submit" variant="primary" disabled={busy || !dirty}>{t('widgets.save')}</Button>}
            </DrawerFooter>
        </DrawerForm>
      )}
    </Drawer>
  );
}

// ---- the screen ----------------------------------------------------------------------------------------------------

export function WidgetsScreen(): ReactElement {
  const { runtime, grants } = useAdmin();
  const { t, tp } = useT();
  const { v, fail } = useVocab();
  const { toast } = useConsole();
  const when = useWhen();
  const { mayManage } = widgetAuthority(grants);
  const [rows, setRows] = useState<readonly WidgetEmbedView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | undefined>(undefined);

  const load = useCallback(async (): Promise<void> => {
    setError(undefined);
    const res = await runtime.widgets.list();
    if (res.ok) setRows(res.value.embeds);
    else {
      setRows(undefined);
      setError(t('widgets.error.list', { code: fail(res.error) }));
    }
  }, [runtime, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const upsert = useCallback((view: WidgetEmbedView): void => {
    setRows((prev) => {
      const list = prev ?? [];
      return list.some((r) => r.embedId === view.embedId) ? list.map((r) => (r.embedId === view.embedId ? view : r)) : [view, ...list];
    });
  }, []);

  const open = rows?.find((r) => r.embedId === openId);
  const loading = rows === undefined && error === undefined;

  return (
    <>
      <PageHeader
        title={t('widgets.title')}
        subtitle={t('widgets.subtitle')}
        actions={mayManage ? <Button variant="primary" onClick={() => setCreating(true)}>{t('widgets.new')}</Button> : undefined}
      />
      {!mayManage && (
        <Notice tone="neutral">{t('widgets.readOnly')}</Notice>
      )}
      {error !== undefined && (
        <Stack gap={3} align="start">
          <Notice tone="danger">{error}</Notice>
          <Button variant="secondary" onClick={() => void load()}>{t('widgets.retry')}</Button>
        </Stack>
      )}
      {error === undefined && (
        <ListTable<WidgetEmbedView>
          columns={[
            { key: 'name', header: t('widgets.col.name'), width: 'minmax(0, 1.4fr)', render: (r) => (widgetNeedsMarket(r) ? <Cluster gap={2}><Text role="label">{r.name}</Text><Badge tone="danger">{t('widgets.market.missing.badge')}</Badge></Cluster> : <Text role="label">{r.name}</Text>) },
            { key: 'key', header: t('widgets.col.key'), width: 'minmax(0, 1fr)', render: (r) => <Text mono>{r.keyDisplay}</Text> },
            {
              key: 'sites',
              header: t('widgets.col.sites'),
              width: 'minmax(0, 1.4fr)',
              render: (r) => {
                const s = originsSummary(r.allowedOrigins);
                return (
                  <Text title={r.allowedOrigins.join('\n')}>
                    {s.first}
                    {s.more > 0 && <Text tone="tertiary" mono> {t('widgets.sites.more', { count: s.more })}</Text>}
                  </Text>
                );
              },
            },
            { key: 'status', header: t('widgets.col.status'), width: 'max-content', render: (r) => <Badge tone={widgetStatusTone(r.status)}>{v('widget.embed_status', r.status)}</Badge> },
            { key: 'updated', header: t('widgets.col.updated'), width: 'max-content', render: (r) => <span className="tnum">{when(r.updatedAt)}</span> },
          ]}
          rows={rows ?? []}
          rowKey={(r) => r.embedId}
          onRowClick={(r) => setOpenId(r.embedId)}
          {...(openId !== undefined ? { selectedKey: openId } : {})}
          {...(rows !== undefined ? { countLabel: tp('widgets.count', rows.length, { count: rows.length }) } : {})}
          empty={
            loading ? (
              <LoadingBlock />
            ) : (
              <EmptyResult title={t('widgets.empty.title')} detail={mayManage ? t('widgets.empty.detail') : t('widgets.empty.detailReadOnly')} />
            )
          }
        />
      )}

      {mayManage && (
        <CreateWidgetDrawer
          open={creating}
          onClose={() => setCreating(false)}
          onCreated={(view) => {
            upsert(view);
            setCreating(false);
            setOpenId(view.embedId);
            toast({ message: t('widgets.create.done'), tone: 'success' });
          }}
        />
      )}
      <WidgetDetailDrawer embed={open} mayManage={mayManage} onClose={() => setOpenId(undefined)} onChanged={upsert} />
    </>
  );
}
